<template>
    <client-page>
        <mypage-layout titleSpacingLg title="장바구니">
            <cart-tabs v-bind="{ summary }" />

            <template v-if="carts.length">
                <cart-head v-model="selected" v-bind="{ carts, carts__outOfStock }" v-on="{ removeSelected, removeDisabled }" />

                <!-- S: cart-body -->
                <div class="border-t border-2 border-dark">
                    <v-row no-gutters v-for="{ shipping, deliveryPrice, deliveryMessage, groups } in cartMaps" :key="shipping.code">
                        <v-col cols="12" xl="">
                            <template v-for="item in groups">
                                <cart-item v-model="selected" v-bind="{ ...item, deliveryType }" v-on="{ remove, search }" :key="item._id" />
                            </template>
                        </v-col>
                        <v-col cols="12" xl="2" class="d-none d-xl-block" v-if="deliveryType != 'stock'">
                            <div class="d-flex align-center h-100 border-b pa-12px px-xl-20px py-md-16px">
                                <v-row class="row--xxs text-center">
                                    <v-col cols="12">
                                        <div class="txt txt--xs">배송비</div>
                                    </v-col>
                                    <v-col cols="12">
                                        <strong class="d-inline-flex align-center font-size-18 font-size-md-20">
                                            {{ deliveryPrice?.format?.() }}<small class="font-size-14 font-weight-regular pl-2px">원</small>
                                            <v-tooltip bottom>
                                                <template v-slot:activator="{ on, attrs }">
                                                    <v-icon v-bind="attrs" v-on="on" small color="grey lighten-3" class="ml-4px cursor-pointer">mdi-help-circle-outline</v-icon>
                                                </template>
                                                <span>{{ deliveryMessage }}</span>
                                            </v-tooltip>
                                        </strong>
                                    </v-col>
                                </v-row>
                            </div>
                        </v-col>
                    </v-row>
                </div>
                <!-- E: cart-body -->

                <cart-cycle v-model="deliveryCycle" v-bind="{ deliveryType }" />

                <div class="pt-24px px-md-16px">
                    <cart-foot v-bind="{ selected, deliveryType }" v-on="{ order }" />
                </div>

                <div class="btn-wrap btn-wrap--lg" v-if="deliveryType != 'regular'">
                    <v-card rounded="" elevation="4">
                        <div class="pa-18px pa-md-24px">
                            <v-row align="center" justify="center">
                                <v-col cols="auto">
                                    <div class="d-inline-flex align-center">
                                        <div class="txt txt--xs pr-10px pr-md-16px">총 주문금액</div>
                                        <strong class="font-size-20 font-size-md-36 primary--text"> {{ totalPrice?.format?.() || 0 }}<small class="font-size-18 font-size-md-20 font-weight-regular pl-2px">원</small> </strong>
                                    </div>
                                </v-col>
                                <v-col cols="12" md="auto">
                                    <v-btn x-large color="primary" class="v-size--xx-large w-100 min-w-sm-200px" @click="order(carts)">전체 주문하기</v-btn>
                                </v-col>
                            </v-row>
                        </div>
                    </v-card>
                </div>
            </template>

            <template v-else>
                <div class="text-center mt-20px mt-md-30px mt-lg-60px">
                    <div class="mb-18px mb-md-24px">
                        <v-icon size="60px" color="primary">mdi-cart</v-icon>
                    </div>
                    <div class="tit tit--sm">장바구니에 담긴 상품이 없습니다.</div>
                    <div class="txt txt--sm mt-8px">원하시는 상품을 담아주세요.</div>

                    <div class="btn-wrap btn-wrap--lg">
                        <v-row justify="center" class="row--sm">
                            <v-col cols="6" sm="auto">
                                <v-btn color="primary" to="/shop" class="v-size--xx-large w-100 min-w-sm-200px">쇼핑 계속하기</v-btn>
                            </v-col>
                        </v-row>
                    </div>
                </div>
            </template>
        </mypage-layout>

        <card-form ref="cardForm" @step2="$refs.cardInputForm.open()" />
        <card-input-form v-model="cardInfo" ref="cardInputForm" @step3="$refs.simplePaymentPassword.open()" />
        <simple-payment-password v-model="cardInfo" ref="simplePaymentPassword" @next="$refs.confirmSimplePaymentPassword.open()" @prev="$refs.cardInputForm.open()" />
        <confirm-simple-payment-password v-model="cardInfo" ref="confirmSimplePaymentPassword" @next="createBillKey" @prev="$refs.simplePaymentPassword.open()" />
        <card-success ref="cardSuccess" @next="order(selected)" />
    </client-page>
</template>

<script>
import api from "@/api";
import { mapMutations, mapState } from "vuex";

import ClientPage from "@/components/client/templates/client-page.vue";
import MypageLayout from "@/components/client/templates/mypage-layout.vue";

import CartCycle from "@/components/client/mypage/cart/cart-cycle.vue";
import CartFoot from "@/components/client/mypage/cart/cart-foot.vue";
import CartHead from "@/components/client/mypage/cart/cart-head.vue";
import CartItem from "@/components/client/mypage/cart/cart-item.vue";
import CartTabs from "@/components/client/mypage/cart/cart-tabs.vue";

import CardForm from "@/components/client/mypage/card/card-form.vue";
import CardInputForm from "@/components/client/mypage/card/card-input-form.vue";
import SimplePaymentPassword from "@/components/client/mypage/card/simple-payment-password.vue";
import ConfirmSimplePaymentPassword from "@/components/client/mypage/card/confirm-simple-payment-password.vue";
import CardSuccess from "@/components/client/mypage/card/card-success.vue";

const initSummary = () => ({
    singleCartTotalCount: 0,
    regularCartTotalCount: 0,
    stockCartTotalCount: 0,
});

export default {
    components: {
        ClientPage,
        MypageLayout,

        CartCycle,
        CartFoot,
        CartHead,
        CartItem,
        CartTabs,

        CardForm,
        CardInputForm,
        SimplePaymentPassword,
        ConfirmSimplePaymentPassword,
        CardSuccess,
    },
    props: {
        deliveryType: { type: String, default: "single" },
    },
    data: () => ({
        summary: initSummary(),

        carts: [],
        selected: [],

        cardInfo: {
            name: null,
            cardNo: null,
            expYear: null,
            expMonth: null,
            idNo: null,
            cardPw: null,
            simplePassword: null,
            confirmSimplePassword: null,
        },

        deliveryCycle: "1week",

        loading: false,
    }),
    computed: {
        ...mapState(["accessToken", "shippings"]),
        cartMaps() {
            return this.$getCartMaps(this.carts).map((map) => {
                const carts = map.groups.flatMap(({ carts }) => carts);
                const deliveryPrice = this.$getDeliveryPrice(carts, this.shippings, this.deliveryType);
                return { ...map, deliveryPrice };
            });
        },
        productPirce() {
            return this.$getProductPrice(this.carts);
        },
        discountPirce() {
            return this.$getDiscountPrice(this.carts);
        },
        deliveryPrice() {
            return this.$getDeliveryPrice(this.carts, this.shippings, this.deliveryType);
        },
        totalPrice() {
            return this.productPirce - this.discountPirce + this.deliveryPrice;
        },
        carts__outOfStock() {
            return this.selected.filter(({ stock = 0 }) => stock <= 0);
        },
    },
    methods: {
        ...mapMutations(["setCarts"]),

        async init() {
            let path = `/mypage/cart/${this.deliveryType}`;
            if (!this.$route.path != path) this.$router.replace(path);

            try {
                this.carts = [];
                this.selected = [];
                await this.search();
            } catch (error) {
                this.$handleError(error);
            } finally {
                this.selected = [...this.cartMaps.flatMap(({ groups }) => groups.flatMap(({ carts }) => carts))];
            }
        },

        async search() {
            if (this.loading) return;
            else this.loading = true;

            try {
                let params = {
                    deliveryType: this.deliveryType,
                };
                if (!this.accessToken) params._carts = (this.$store?.state?.carts || []).map(({ _id }) => _id);

                let { carts, summary } = await api.v1.me.carts.gets({ params });
                this.carts = carts?.filter?.(({ product }) => product);
                this.summary = summary;
                this.setCarts({ carts: this.carts });
            } catch (error) {
                this.$handleError(error);
            } finally {
                this.loading = false;
                this.selected = [...this.cartMaps.flatMap(({ groups }) => groups.flatMap(({ carts }) => carts))];
            }
        },

        async order(selected = []) {
            try {
                if (!selected.length) throw new Error("구매하실 상품을 선택해주세요");

                let route = `/shop/order?deliveryType=${this.deliveryType}`;

                switch (this.deliveryType) {
                    case "regular": {
                        if (!this.deliveryCycle) throw new Error("배송주기를 선택해주세요");

                        route += `&deliveryCycle=${this.deliveryCycle}`;

                        let { card } = await api.v1.me.cards.get();
                        if (!card) {
                            this.$refs.cardForm.open();
                            return;
                        }
                        break;
                    }
                }

                const _carts = selected.map(({ _id }) => _id);
                this.$store.dispatch("pickup", _carts);

                this.$router.push(route);
            } catch (error) {
                this.$handleError(error);
            }
        },

        async remove(item) {
            if (this.loading) return;
            else this.loading = true;

            try {
                await api.v1.me.carts.delete(item);
            } finally {
                this.loading = false;
                this.init();
            }
        },

        async removeSelected() {
            if (this.loading) return;
            else this.loading = true;
            try {
                for (const cart of this.selected) {
                    await api.v1.me.carts.delete(cart);
                }
            } finally {
                this.loading = false;
                this.init();
            }
        },

        async removeDisabled() {
            if (this.loading) return;
            else this.loading = true;

            try {
                for (const cart of carts__outOfStock) {
                    await api.v1.me.carts.delete(cart);
                }
            } finally {
                this.loading = false;
                this.init();
            }
        },

        async createBillKey() {
            let cardInfo = {
                ...this.cardInfo,
                simplePassword: CryptoAES.encrypt(this.cardInfo.simplePassword),
                confirmSimplePassword: CryptoAES.encrypt(this.cardInfo.confirmSimplePassword),
            };

            let { success } = await api.v1.me.cards.post(cardInfo);
            if (success) {
                this.$refs.cardSuccess.open("cart");
            }
        },
    },
    watch: {
        deliveryType() {
            this.init();
        },
    },
    mounted() {
        this.init();
    },
};
</script>
